<!--
  The help widget, bottom left corner. It replaces Crisp. With the left
  sidebar on, the pill hides and the sidebar's Help item opens the panel.

  Crisp showed a live chat window and nobody was behind it. This one never
  claims to be a conversation: a labelled pill, a short note, a Send button.
  The receipt says the answer comes by email, so nobody waits at the window.

  Signed-in members only. The layouts decide that, so this component assumes a
  current user and reads the reply address off the account. A page that needs
  the corner (the video call) hides it through useHiddenSupportWidget.
-->
<template>
  <div
    v-if="!supportWidgetHidden"
    class="pointer-events-none fixed inset-0 z-40"
  >
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-2 opacity-0"
    >
      <div
        v-if="open"
        class="gm-support-panel pointer-events-auto absolute bottom-20 left-6 w-[360px] max-w-[calc(100vw-3rem)] rounded-2xl bg-white p-5 shadow-[0_16px_48px_rgba(15,23,42,0.18)] ring-1 ring-neutral-900/10"
      >
        <button
          type="button"
          class="absolute top-4 right-4 text-neutral-400 transition hover:text-neutral-700"
          aria-label="Close"
          @click="open = false"
        >
          <svg
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <path d="M5 5l10 10M15 5L5 15" />
          </svg>
        </button>

        <div v-if="sent">
          <div
            class="bg-success-50 flex h-10 w-10 items-center justify-center rounded-full"
          >
            <svg
              class="text-success-700 h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <h3 class="mt-3 text-base font-semibold text-neutral-900">
            We'll get back to you soon
          </h3>
          <p class="mt-1.5 text-sm leading-relaxed text-neutral-600">
            We'll reply to
            <strong class="font-medium text-neutral-900">{{ replyTo }}</strong>
            within a few hours, usually much sooner (unless we're sleeping).
          </p>
          <p
            class="mt-3 rounded-xl bg-neutral-50 px-3.5 py-3 text-sm leading-relaxed whitespace-pre-wrap text-neutral-600"
          >
            {{ sentMessage }}
          </p>
          <button
            type="button"
            class="mt-4 w-full rounded-xl border border-neutral-200 py-2.5 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50"
            @click="reset"
          >
            Have more to say?
          </button>
        </div>

        <form v-else @submit.prevent="submit">
          <div class="flex items-center gap-3">
            <div class="flex -space-x-2">
              <img
                v-for="face in FACES"
                :key="face.src"
                :src="face.src"
                :alt="face.alt"
                class="h-8 w-8 rounded-full object-cover ring-2 ring-white"
              />
            </div>
            <div>
              <h3 class="text-base font-semibold text-neutral-900">
                Need a hand?
              </h3>
              <p class="text-xs text-neutral-500">
                Foti and Jessica read these
              </p>
            </div>
          </div>

          <p class="mt-3 text-sm leading-relaxed text-neutral-600">
            Tell us what happened. This goes to our inbox as an email and we
            answer within one business day.
          </p>

          <textarea
            ref="input"
            v-model="message"
            rows="4"
            :disabled="sending"
            placeholder="A session didn't go through, a charge looks wrong, something's broken…"
            class="focus:border-primary-400 focus:ring-primary-100 mt-3 w-full resize-none rounded-xl border border-neutral-200 px-3.5 py-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:ring-2 disabled:opacity-60"
          />
          <p v-if="error" class="text-error-500 mt-1.5 text-xs">{{ error }}</p>

          <button
            type="submit"
            class="mt-3 w-full rounded-xl bg-neutral-900 py-3 text-sm font-semibold text-white transition hover:bg-neutral-800 disabled:opacity-40"
            :disabled="sending || !message.trim()"
          >
            {{ sending ? 'Sending…' : 'Send' }}
          </button>

          <p class="mt-2.5 text-center text-xs text-neutral-400">
            Reply goes to {{ replyTo }}
          </p>
        </form>
      </div>
    </Transition>

    <button
      v-if="!inSidebar"
      type="button"
      class="pointer-events-auto absolute bottom-6 left-6 flex items-center gap-2.5 rounded-full bg-white py-2.5 pr-5 pl-2.5 shadow-[0_8px_24px_rgba(15,23,42,0.16)] ring-1 ring-neutral-900/10 transition hover:shadow-[0_10px_28px_rgba(15,23,42,0.22)] active:scale-95"
      @click="toggle"
    >
      <span class="flex -space-x-2">
        <img
          v-for="face in FACES"
          :key="face.src"
          :src="face.src"
          :alt="face.alt"
          class="h-7 w-7 rounded-full object-cover ring-2 ring-white"
        />
      </span>
      <span class="text-sm font-semibold text-neutral-900">
        {{ open ? 'Close' : 'Need help?' }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  useTemplateRef,
  watch,
} from 'vue'
import axios from 'axios'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import {
  supportWidgetHidden,
  supportWidgetOpen,
} from '@/shared/composables/useHiddenSupportWidget'

const FACES = [
  { src: '/email-photos/f2-face-jessica.jpg', alt: 'Jessica' },
  { src: '/email-photos/f2-face-foti.jpg', alt: 'Foti' },
]

const { currentUser } = useCurrentUser()

// With the left sidebar on, the pill does not show: Help opens from the
// sidebar through the shared `supportWidgetOpen`.
defineProps<{ inSidebar?: boolean }>()

const open = supportWidgetOpen
const message = ref('')
const sentMessage = ref('')
const sent = ref(false)
const sending = ref(false)
const error = ref('')
const input = useTemplateRef<HTMLTextAreaElement>('input')

const replyTo = computed(() => currentUser.value?.email ?? '')

const toggle = () => {
  open.value = !open.value
}

// The open state is shared with the sidebar, so it outlives this component.
// Close it on unmount, or it would come back open on the next layout.
onBeforeUnmount(() => {
  open.value = false
})

// Focus the message box on every open, from the pill or from the sidebar.
watch(open, async (isOpen) => {
  if (!isOpen) return
  await nextTick()
  input.value?.focus()
})

const submit = async () => {
  const body = message.value.trim()
  if (!body) {
    error.value = 'Write a message first.'
    return
  }

  error.value = ''
  sending.value = true

  try {
    await axios.post('/support_messages', {
      body,
      page_url: window.location.href,
    })
    sentMessage.value = body
    message.value = ''
    sent.value = true
  } catch (e) {
    error.value =
      (axios.isAxiosError(e) && e.response?.data?.errors?.base) ||
      'That did not send. Try again, or email support@growthmentor.com.'
  } finally {
    sending.value = false
  }
}

const reset = async () => {
  sent.value = false
  await nextTick()
  input.value?.focus()
}
</script>
